/**
 * Projection for `PopulationScene` — the demo's `renderPopulation()` (line
 * 2804) and `exportPopulation()` (line 2809).
 *
 * Pure: no React, no fetching, no engine mutation, no `Date`. The register is
 * the eager fleet table joined to the condition register and, for the twelve
 * connected surface drills, to the RMS fleet status.
 *
 * Transformation layer (layer 3 of the 4-layer architecture).
 *
 * @see src/versions/v1/modules/service/PopulationScene.tsx
 * @see docs/logic/workflows/population.md
 * @see docs/foundations/4-layer-architecture.md
 */

import { FLEET, MINES, WORKINGS } from '../../data/core';
import type { ComponentHealth } from '../../data/core';
import type { ConditionBand, Machine, MachineClass, MineCode, ModelCode } from '../../schema/machine';
import { CONDITION_BAND_LABEL, CONDITION_BAND_ORDER } from '../../schema/machine';
import { healthOf, statusOf } from '../../shared/oem-engine';
import type { RmsStatusRow } from '../../shared/useOemData';

/** Demo line 2804. A hot spot is a component scoring 40 or below. */
export const HOT_SPOT_MAX = 40;

/**
 * Demo line 2803. `BRIDGING` — the four machines standing in for something
 * else, and why. Authored copy, not derived.
 */
export const BRIDGING: Readonly<Record<string, string>> = {
  'S-11':
    'Bridging unit — pre-split on the Stage 3 cutback until the second MS-45 is commissioned (Nov)',
  'L-16':
    'Bridging unit — ML-14 covering the northern stopes while L-18 was in repair (07–09 Sep)',
  'L-01': 'Bridging unit — ML-14 on the 280 level while L-02 was flushed (03 Sep)',
  'T-05':
    'Bridging unit — on loan from Broken Ridge to Kalbarri Mar–Jun 2026, returned',
};

/** What the register needs loaded. */
export interface PopulationCtx {
  /** `useOemData('rms').rms_status` — the twelve connected surface drills. */
  readonly rmsStatus: readonly RmsStatusRow[];
}

/** Demo line 2804. One serial number's row. */
export interface PopulationRow {
  readonly machine: Machine;
  readonly band: ConditionBand;
  readonly bandLabel: string;
  /** Components scoring at or below {@link HOT_SPOT_MAX}. */
  readonly hotSpots: readonly ComponentHealth[];
  /** Where it is: the drill pattern and hole, the named working, or the default. */
  readonly location: string;
  /** What it is doing. */
  readonly activity: string;
  /** `'05:53 (RMS)'`, `'06:04 (live)'` or `'06:05 (shift-end)'`. */
  readonly lastHeard: string;
  /** Site display name. */
  readonly siteName: string;
  /** The demo prints the first word of the site name in the table. */
  readonly siteShort: string;
  /** Bridging note, or `null`. */
  readonly bridging: string | null;
}

/**
 * Demo line 2804. The "last heard" clock on a machine with no RMS link.
 *
 * A live site fabricates a minute from the machine id's own character codes,
 * which keeps the column from being a column of one repeated value while
 * staying deterministic — the frozen snapshot must render identically every
 * time. A shift-end site reports the batch export, 06:05.
 */
function lastHeardOf(f: Machine, rms: RmsStatusRow | undefined): string {
  if (rms) return `${rms.last_heard.slice(11, 16)} (RMS)`;
  if (MINES[f.mine].data_mode === 'Live') {
    return `06:0${(f.machine_id.charCodeAt(2) + f.machine_id.charCodeAt(3)) % 9} (live)`;
  }
  return '06:05 (shift-end)';
}

/** Demo line 2804. Where the machine is. */
function locationOf(f: Machine, rms: RmsStatusRow | undefined): string {
  if (rms) return `${rms.pattern} · hole ${rms.hole}`;
  if (f.working && WORKINGS[f.mine]) return f.working;
  return MINES[f.mine].mining === 'Underground' ? 'Main decline' : 'Main pit';
}

/** Demo line 2804. The whole register, in fleet-register order. */
export function populationRows(ctx: PopulationCtx): PopulationRow[] {
  return FLEET.map((f) => {
    const rms = ctx.rmsStatus.find((r) => r.machine_id === f.machine_id);
    const band = statusOf(f.machine_id);
    const site = MINES[f.mine];
    return {
      machine: f,
      band,
      bandLabel: CONDITION_BAND_LABEL[band],
      hotSpots: healthOf(f.machine_id).filter((h) => h.health_score <= HOT_SPOT_MAX),
      location: locationOf(f, rms),
      activity: rms
        ? rms.status
        : band === 'major'
          ? 'Restricted / light duty'
          : 'Operating',
      lastHeard: lastHeardOf(f, rms),
      siteName: site.name,
      siteShort: site.name.split(' ')[0],
      bridging: BRIDGING[f.machine_id] ?? null,
    };
  });
}

// ---------------------------------------------------------------------------
// Filtering
// ---------------------------------------------------------------------------

/** Demo line 2805. `POP_FILT`, carried on the URL rather than in a global. */
export interface PopulationFilter {
  /** Free text over the fleet-register row. */
  readonly q: string;
  readonly mine: MineCode | '';
  readonly cls: MachineClass | '';
  readonly model: ModelCode | '';
  readonly band: ConditionBand | '';
}

/** An unfiltered register. */
export const EMPTY_FILTER: PopulationFilter = { q: '', mine: '', cls: '', model: '', band: '' };

/**
 * Demo line 2805. The filter.
 *
 * The text match is the demo's: the **fleet-register** row serialised and
 * searched, which is why a query hits the serial, the model, the subclass or
 * the operator but not the derived location or activity.
 */
export function filterRows(
  rows: readonly PopulationRow[],
  filter: PopulationFilter,
): PopulationRow[] {
  const q = filter.q.trim().toLowerCase();
  return rows.filter(
    (r) =>
      (!filter.mine || r.machine.mine === filter.mine) &&
      (!filter.cls || r.machine.cls === filter.cls) &&
      (!filter.model || r.machine.model === filter.model) &&
      (!filter.band || r.band === filter.band) &&
      (!q || JSON.stringify(r.machine).toLowerCase().includes(q)),
  );
}

// ---------------------------------------------------------------------------
// Sorting
// ---------------------------------------------------------------------------

/** The sortable columns. */
export const POPULATION_SORTS = [
  'serial',
  'machine',
  'model',
  'site',
  'hours',
  'commissioned',
  'condition',
] as const;

/** A sortable column key. */
export type PopulationSort = (typeof POPULATION_SORTS)[number];

/** Whether a URL value names a sortable column. */
export const isPopulationSort = (value: string | null): value is PopulationSort =>
  (POPULATION_SORTS as readonly string[]).includes(value ?? '');

/**
 * Sort the register.
 *
 * `'register'` is not one of the keys: passing no sort leaves the rows in
 * fleet-register order, which is the demo's and is meaningful (machines are
 * grouped by class and site in the source file). The condition sort is worst
 * first, because a reader sorting by condition is looking for trouble.
 */
export function sortRows(
  rows: readonly PopulationRow[],
  sort: PopulationSort | null,
  descending: boolean,
): PopulationRow[] {
  if (!sort) return [...rows];
  const dir = descending ? -1 : 1;
  const compare = (a: PopulationRow, b: PopulationRow): number => {
    switch (sort) {
      case 'serial':
        return a.machine.serial.localeCompare(b.machine.serial);
      case 'machine':
        return a.machine.machine_id.localeCompare(b.machine.machine_id);
      case 'model':
        return a.machine.model.localeCompare(b.machine.model);
      case 'site':
        return a.siteName.localeCompare(b.siteName);
      case 'hours':
        return a.machine.engine_hours - b.machine.engine_hours;
      case 'commissioned':
        return a.machine.commissioned.localeCompare(b.machine.commissioned);
      case 'condition':
        return CONDITION_BAND_ORDER[a.band] - CONDITION_BAND_ORDER[b.band];
    }
  };
  return [...rows].sort((a, b) => compare(a, b) * dir || a.machine.machine_id.localeCompare(b.machine.machine_id));
}

// ---------------------------------------------------------------------------
// CSV export
// ---------------------------------------------------------------------------

/** Demo line 2809. The export's column order. Deterministic, never derived. */
export const CSV_COLUMNS = [
  'serial',
  'machine_id',
  'model',
  'mine',
  'working',
  'operated_by',
  'engine_hours',
  'commissioned',
  'condition',
  'hot_spots',
] as const;

/** Demo line 2809. The file name, dated to the frozen snapshot. */
export const CSV_FILENAME = 'population_list_2026-09-09.csv';

/** Demo line 2809. One CSV field, double-quoted with quotes doubled. */
const csvField = (value: unknown): string => `"${String(value ?? '').replace(/"/g, '""')}"`;

/**
 * Demo line 2809. `exportPopulation()` — the register as CSV text.
 *
 * One declared deviation: the demo always exports the whole hundred, whatever
 * is filtered on screen. This exports the rows in view, because a reader who
 * has narrowed to Kalbarri's trucks and pressed Export meant those. The header
 * and column order are the demo's.
 */
export function populationCsv(rows: readonly PopulationRow[]): string {
  const lines = [CSV_COLUMNS.join(',')];
  rows.forEach((r) => {
    const f = r.machine;
    lines.push(
      [
        f.serial,
        f.machine_id,
        f.model,
        r.siteName,
        f.working ?? '',
        f.operated_by,
        f.engine_hours,
        f.commissioned,
        r.bandLabel,
        r.hotSpots.map((h) => `${h.component} ${h.health_score}`).join('; '),
      ]
        .map(csvField)
        .join(','),
    );
  });
  return lines.join('\n');
}

// ---------------------------------------------------------------------------
// Header counts and filter vocabularies
// ---------------------------------------------------------------------------

/** Demo line 2806. The header chip. */
export interface PopulationSummary {
  readonly shown: number;
  readonly total: number;
  readonly bridging: number;
  readonly hotSpots: number;
}

/** Demo line 2806. The counts beside the page title. */
export function populationSummary(
  rows: readonly PopulationRow[],
  all: readonly PopulationRow[],
): PopulationSummary {
  return {
    shown: rows.length,
    total: all.length,
    bridging: all.filter((r) => r.bridging).length,
    hotSpots: all.reduce((a, r) => a + r.hotSpots.length, 0),
  };
}

/** The models actually in the population, for the model filter. */
export const populationModels = (): ModelCode[] =>
  [...new Set(FLEET.map((f) => f.model))].sort((a, b) => a.localeCompare(b));
